import { Loader2 } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

interface PuzzleAnswer {
  id: number;
  customPuzzleId: number;
  answer: string;
  createdAt: string;
  user: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
}

interface PuzzleAnswersTabProps {
  puzzleAnswers: PuzzleAnswer[] | undefined;
  isPuzzleAnswersLoading: boolean;
  puzzleId?: number;
  onOpenAnswerDialog?: (puzzleId: number) => void;
  currentUser?: {
    id: number;
  };
  puzzleOwnerId?: number;
  solvedByUserId?: number;
}

export function PuzzleAnswersTab({ 
  puzzleAnswers, 
  isPuzzleAnswersLoading,
  puzzleId,
  onOpenAnswerDialog,
  currentUser,
  puzzleOwnerId,
  solvedByUserId
}: PuzzleAnswersTabProps) {
  const { t } = useTranslation();

  const handleAnswerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (puzzleId && onOpenAnswerDialog) {
      onOpenAnswerDialog(puzzleId);
    }
  };

  const canAddAnswer = 
    puzzleId && 
    onOpenAnswerDialog && 
    currentUser && 
    // Check if the user is either the puzzle owner or the user who solved the puzzle
    ((puzzleOwnerId && puzzleOwnerId === currentUser.id) || 
    (solvedByUserId && solvedByUserId === currentUser.id)) && 
    (!puzzleAnswers || puzzleAnswers.length < 2);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">{t("desafios_cacatais.answers.title", { fallback: "Solutions" })}</h3>
        {canAddAnswer && (
          <Button 
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={handleAnswerClick}
          >
            {t("desafios_cacatais.add_answer", { fallback: "Add Answer" })}
          </Button>
        )}
      </div>
      {isPuzzleAnswersLoading ? (
        <div className="text-center py-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : puzzleAnswers && puzzleAnswers.length > 0 ? (
        <div className="space-y-4">
          {puzzleAnswers.map((answer) => (
            <div key={answer.id} className="bg-zinc-800 p-4 rounded-md">
              <div className="flex items-start gap-3 mb-3">
                {answer.user.avatarUrl ? (
                  <img 
                    src={answer.user.avatarUrl} 
                    alt={answer.user.displayName || "User"} 
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
                    <span className="text-sm text-white">
                      {answer.user.displayName?.charAt(0) || answer.user.email.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <Link 
                    href={`/users/${answer.user.displayName ? answer.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : answer.user.id}`} 
                    className="text-sm font-medium text-white hover:text-blue-400 hover:underline transition-colors"
                  >
                    {answer.user.displayName || answer.user.email.split('@')[0]}
                  </Link>
                  <p className="text-xs text-zinc-500">{format(new Date(answer.createdAt), 'dd/MM/yyyy')}</p>
                </div>
              </div>
              <div className="bg-zinc-900 p-3 rounded-md">
                <code className="block font-mono text-zinc-300 break-all">{answer.answer}</code>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-zinc-400">{t("desafios_cacatais.answers.no_answers", { fallback: "No solutions available yet." })}</p>
        </div>
      )}
    </div>
  );
}
